<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <div class="page-title">
            <span class="page-title">
                <i class="pi pi-stop-circle" />
                <h1>{{ eventName }}</h1>
            </span>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </div>
        <div class="controls">
            <template v-if="isArchived">
                <Button
                    class="p-button-secondary"
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content && isEditing"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateEventDescription"
                />
            </template>
        </div>
    </div>
    <section class="description" :class="{ 'edit': isEditing }">
        <TextAppArtefact
            v-if="event?.identity && description"
            v-model="description"
            :entityId="event.identity"
            :entityType="EntityType.EVENT"
            :isEditing="isEditing"
        />
    </section>
</template>

<script setup lang="ts">
import { baseSuccessToastMessage } from "@/primevue/init";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Spinner from "@/shared/Spinner.vue";
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useEventManager, useEventStore } from "@/stores/event-store";
import { useEditManager } from "@/composables/edit-manager";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";

const toast = useToast();
const { deleteEvent } = useEventStore();
const { isEditing } = useEditManager();
const { isFetching, description, event } = useEventManager();
const { generateContent } = useGenerateDescriptionDialog();

const confirm = useConfirm();

const displaySpinner = ref(false);

const isArchived = computed(() => {
    return event.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete event. Event is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this event?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteEvent();
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Event has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete event",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const eventName = computed(() => {
    if (!event.value) {
        return "...";
    }

    return event.value.name || event.value.bpmn_id;
});

const generateEventDescription = async () => {
    if (!event.value) {
        return;
    }
    await generateContent(
        event.value.identity,
        EntityType.EVENT,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.page-title {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;

    i {
        font-size: 1.6rem;
    }
    .p-tag {
        margin-left: 0.5rem;
    }
}

section {
    border: 1px solid transparent;
    position: relative;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}
</style>
